import React, { useMemo } from 'react';
import {
    Alert,
    Avatar,
    Box,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Tooltip,
    Typography,
    Chip
} from '@mui/material';
import {
    History as HistoryIcon,
    Person as PersonIcon,
    Update as UpdateIcon,
    ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useValuations } from '../../../hooks/administration/useValuations';

const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    return new Intl.DateTimeFormat('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const getUserName = (row) => {
    const fullName = [row?.pernom, row?.perape].filter(Boolean).join(' ').trim();
    if (fullName) return fullName;
    if (row?.empid) return `Empleado ${row.empid}`;
    if (row?.usuid) return `Usuario ${row.usuid}`;
    return 'Usuario no identificado';
};

const getInitials = (row) => {
    const fullName = getUserName(row);
    if (!fullName || fullName.startsWith('Usuario') || fullName.startsWith('Empleado')) {
        return 'U';
    }

    const parts = fullName.split(' ').filter(Boolean);
    return parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
};

const getActionTone = (text = '') => {
    const message = String(text).toLowerCase();

    if (
        message.includes('eliminad') ||
        message.includes('cancelad') ||
        message.includes('retirada') ||
        message.includes('retirad')
    ) {
        return {
            bg: '#ffebee',
            border: '#ef9a9a',
            iconBg: '#d32f2f',
            label: 'Acción sensible',
        };
    }

    if (
        message.includes('facturada') ||
        message.includes('cobrada') ||
        message.includes('aprobada') ||
        message.includes('estado')
    ) {
        return {
            bg: '#ede7f6',
            border: '#b39ddb',
            iconBg: '#6a1b9a',
            label: 'Cambio de estado',
        };
    }

    if (
        message.includes('creada') ||
        message.includes('generada')
    ) {
        return {
            bg: '#e8f5e9',
            border: '#a5d6a7',
            iconBg: '#2e7d32',
            label: 'Creación',
        };
    }

    if (
        message.includes('recalcul') ||
        message.includes('actualiz') ||
        message.includes('ajuste') ||
        message.includes('agregada') ||
        message.includes('agregado')
    ) {
        return {
            bg: '#e3f2fd',
            border: '#90caf9',
            iconBg: '#1976d2',
            label: 'Actualización',
        };
    }

    return {
        bg: '#f5f5f5',
        border: '#e0e0e0',
        iconBg: '#607d8b',
        label: 'Evento',
    };
};

const groupHistoryByDate = (rows = []) => {
    return rows.reduce((acc, row) => {
        const date = row?.credat ? row.credat.slice(0, 10) : 'sin-fecha';
        if (!acc[date]) acc[date] = [];
        acc[date].push(row);
        return acc;
    }, {});
};

const formatOnlyDate = (dateText) => {
    if (!dateText || dateText === 'sin-fecha') return 'Sin fecha';
    const date = new Date(`${dateText}T00:00:00`);
    return new Intl.DateTimeFormat('es-PE', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    }).format(date);
};

const ValuationHistorySection = ({ valuationId }) => {
    const { useValuationHistory } = useValuations();

    const {
        data: history = [],
        isLoading,
        isFetching,
        error,
    } = useValuationHistory(valuationId);

    const groupedHistory = useMemo(() => {
        return groupHistoryByDate(history);
    }, [history]);

    if (!valuationId) {
        return (
            <Alert severity="warning">
                No se pudo cargar el historial porque no existe un ID de valorización válido.
            </Alert>
        );
    }

    if (isLoading) {
        return (
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
                    <Stack alignItems="center" spacing={1.5}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            Cargando historial...
                        </Typography>
                    </Stack>
                </Box>
            </Paper>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {error?.message || 'Error al cargar el historial de la valorización.'}
            </Alert>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={1.5}
                mb={2.5}
            >
                <Box>
                    <Typography variant="h6" fontWeight="bold">
                        Historial de la valorización
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Registro cronológico de acciones realizadas sobre este documento.
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <HistoryIcon color="primary" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        {history.length} evento(s)
                    </Typography>
                    {isFetching && (
                        <CircularProgress size={16} />
                    )}
                </Box>
            </Box>

            {history.length === 0 ? (
                <Alert severity="info" variant="outlined">
                    Esta valorización aún no tiene eventos registrados en su historial.
                </Alert>
            ) : (
                <Stack spacing={3}>
                    {Object.entries(groupedHistory).map(([dateKey, events]) => (
                        <Box key={dateKey}>
                            <Box mb={1.5}>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    sx={{ textTransform: 'capitalize' }}
                                >
                                    {formatOnlyDate(dateKey)}
                                </Typography>
                            </Box>

                            <Stack spacing={1.5}>
                                {events.map((item, index) => {
                                    const tone = getActionTone(item?.hisacc);
                                    const initials = getInitials(item);
                                    const userName = getUserName(item);

                                    return (
                                        <Box
                                            key={item.hisid || `${dateKey}-${index}`}
                                            display="flex"
                                            gap={1.5}
                                            alignItems="flex-start"
                                        >
                                            {/* Línea temporal */}
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                minWidth={44}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        fontSize: 13,
                                                        fontWeight: 'bold',
                                                        bgcolor: tone.iconBg,
                                                    }}
                                                >
                                                    {initials}
                                                </Avatar>

                                                {index !== events.length - 1 && (
                                                    <Box
                                                        sx={{
                                                            width: 2,
                                                            flexGrow: 1,
                                                            minHeight: 26,
                                                            bgcolor: '#e0e0e0',
                                                            mt: 1,
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Contenido */}
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    flex: 1,
                                                    p: 1.8,
                                                    borderRadius: 2.5,
                                                    backgroundColor: tone.bg,
                                                    borderColor: tone.border,
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems={{ xs: 'flex-start', md: 'center' }}
                                                    flexDirection={{ xs: 'column', md: 'row' }}
                                                    gap={1}
                                                    mb={1}
                                                >
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Chip
                                                            size="small"
                                                            icon={<UpdateIcon sx={{ fontSize: '16px !important' }} />}
                                                            label={tone.label}
                                                            variant="outlined"
                                                        />
                                                        <Tooltip title={`Registrado por: ${userName}`}>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <PersonIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {userName}
                                                                </Typography>
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>

                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDateTime(item?.credat)}
                                                    </Typography>
                                                </Box>

                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {item?.hisacc || 'Sin descripción'}
                                                </Typography>

                                                {(item?.usuid || item?.empid) && (
                                                    <>
                                                        <Divider sx={{ my: 1.2 }} />
                                                        <Box display="flex" alignItems="center" gap={0.8}>
                                                            <ReceiptLongIcon
                                                                fontSize="inherit"
                                                                sx={{ color: 'text.secondary' }}
                                                            />
                                                            <Typography variant="caption" color="text.secondary">
                                                                Usuario relacionado: {item?.usuid || item?.empid}
                                                            </Typography>
                                                        </Box>
                                                    </>
                                                )}
                                            </Paper>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </Paper>
    );
};

export default ValuationHistorySection;